/** @typedef {import('./Card.js').Card} Card */

/**
 * A Card Bucket to store Cards of a certain label type.
 */

export class CardBucket {
  #bucket;

  /**
   * @param {Card[]} [inputCards] - List of Cards in order received from database.
   */
  constructor(inputCards) {
    this.#bucket = inputCards || [];
  }

  /**
   * Add a Card to bucket.
   *
   * @param {Card} card - Card to add.
   */
  push(card) {
    this.#bucket.push(card);
  }

  /**
   * Filters cards upon a non-empty array of tags.
   *
   * @param {*} tags - Tags to filter upon.
   */
  filterOnTags(tags) {
    if (tags !== undefined && tags.length > 0) {
      const tagSet = new Set(tags);
      this.#bucket = this.#bucket.filter((card) => card.getProperty('tags').some((tag) => tagSet.has(tag)));
    }
  }

  /**
   * Filters cards upon a non-empty array of severities.
   *
   * @param {*} severities - Severities to filter upon.
   */
  filterOnSeverities(severities) {
    if (severities !== undefined && severities.length > 0) {
      const severitySet = new Set(severities);
      this.#bucket = this.#bucket.filter((card) => {
        const sev = card.getProperty('severity');
        return severitySet.has(sev === null || sev === undefined ? 'null' : String(sev));
      });
    }
  }

  /**
   * Filters cards upon an array of validation options.
   *
   * @param {*} validationOptions - Validation Options to filter upon.
   */
  filterOnValidationOptions(validationOptions) {
    const validationOptionsSet = new Set(validationOptions);
    this.#bucket = this.#bucket.filter((card) => validationOptionsSet.has(card.getProperty('correctness')));
  }

  /**
   * Return all Cards in bucket.
   */
  getCards() {
    return this.#bucket;
  }

  /**
   * Return how many Cards are in bucket.
   */
  getSize() {
    return this.#bucket.length;
  }

  /**
   * Return a copy of this CardBucket. This is not a deepcopy (the cards themselves are not copied).
   */
  copy() {
    return new CardBucket([...this.#bucket]);
  }

  getCardByIndex(index) {
    return this.#bucket[index];
  }

  findCardIndexByImageId(imageId) {
    for (let i = 0; i < this.#bucket.length; i++) {
      if (this.#bucket[i].getImageId() === imageId) {
        return i;
      }
    }

    return -1;
  }
}
